import { defineStore } from 'pinia';
import { login as loginService, recuperarClave, nuevaClave, miUsuario, loginGoogle, resetPageLoadCache } from '@/services/usuarios';
import router from '@/router';
import { Buffer } from 'safe-buffer';
import strapiService from '@/services/strapi';

// Funcion que decide si debe utilizar el router para redireccionar o es una url externa
const redireccionar = (url) => {
    if (url.startsWith('http')) {
        window.location.href = url;
    } else {
        router.push({ path: url });
    }
}

const setCookie = (cname, cvalue, exdays, domain) => {
    document.cookie = cname + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";

    let d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = "expires=" + d.toUTCString();
    let cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    if (domain) cookie += ";domain=" + domain;
    document.cookie = cookie;
};

const getCookie = (name) => {
    console.log('🔍 Buscando cookie:', name);
    console.log('📄 Document.cookie completo:', document.cookie);
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    
    if (parts.length === 2) {
        const token = parts.pop().split(";").shift();
        console.log(`✅ Cookie ${name} encontrada:`, token ? 'EXISTS' : 'MISSING');
        return token;
    }
    
    console.log(`❌ Cookie ${name} no encontrada`);
    return null;
};

export const useAuthStore = defineStore('auth', {
    state: () => ({
        usuario: null,
        token: null,
        error: null,
        redireccionLogin: null,
        datosAdicionales: null,
        isAuthenticated: false
    }),

    getters: {
        usuarioActual: (state) => state.usuario,
        estaAutenticado: (state) => state.isAuthenticated && state.usuario !== null
    },

    actions: {
        setAfterLogin(url) {
            this.redireccionLogin = url;
        },
        establecerToken(nuevoToken) {
            this.token = nuevoToken;
            const tokenCodificado = Buffer.from(`${this.usuario.id}:${nuevoToken}`).toString("base64");
            setCookie('orsai_token', tokenCodificado, 1, 'orsai.org');
            console.log('✅ Token establecido:', { token: nuevoToken ? 'EXISTS' : 'MISSING' });
        },

        establecerUsuario(datosUsuario) {
            console.log('Estableciendo usuario en el store:', {
                id: datosUsuario?.id,
                email: datosUsuario?.email,
                usuarioCompleto: datosUsuario
            });
            this.usuario = datosUsuario;
            this.isAuthenticated = !!datosUsuario;
            
            // Notificar cambio para reactividad
            console.log('🔄 Usuario establecido, notificando cambio:', {
                usuario: datosUsuario?.email,
                isAuthenticated: this.isAuthenticated
            });
        },

        establecerRedireccionLogin(url) {
            this.redireccionLogin = url;
        },

        limpiarSesion() {
            this.usuario = null;
            this.token = null;
            this.error = null;
            this.redireccionLogin = null;
            this.datosAdicionales = null;
            this.isAuthenticated = false;
        },

        async iniciarSesion(credenciales, redireccion) {
            this.error = null;
            try {
                const respuesta = await loginService(credenciales);
                if (respuesta.login) {
                    this.establecerUsuario(respuesta.usuario);
                    this.establecerToken(respuesta.usuario.token);
                    const datosActualizados = await miUsuario();
                    this.establecerUsuario(datosActualizados);
                    if (redireccion) {
                        router.push(redireccion);
                    } else if (this.redireccionLogin) {
                        router.push(this.redireccionLogin);
                        this.redireccionLogin = null;
                    } else {
                        router.push('/mi-suscripcion');
                    }
                    
                    return true;
                } else {
                    this.limpiarSesion();
                    this.error = "Correo o contraseña incorrectos. Por favor, intentá nuevamente.";
                    return false;
                }
            } catch (e) {
                this.error = "Error al iniciar sesión. Por favor, intentá nuevamente.";
                return false;
            }
        },

        cerrarSesion() {
            console.log('🚪 Cerrando sesión...');
            
            // Limpiar estado del store
            this.limpiarSesion();
            
            // Limpiar cache
            resetPageLoadCache();
            
            // Limpiar cookies de manera exhaustiva
            console.log('🧹 Limpiando cookies...');
            
            // Método 1: Usando setCookie
            setCookie('orsai_token', '', -1, 'orsai.org');
            setCookie('orsai_token', '', -1, '');
            
            // Método 2: Usando document.cookie directamente
            document.cookie = 'orsai_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.orsai.org';
            document.cookie = 'orsai_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=orsai.org';
            document.cookie = 'orsai_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
            
            console.log('✅ Sesión cerrada y cookies limpiadas');
            console.log('📄 Cookies después de limpiar:', document.cookie);
            
            router.push('/ingresar');
        },

        async inicializarAuth() {
            console.log('🔄 Inicializando autenticación...');
            
            // Buscar token en cookies
            const tokenCookie = getCookie('orsai_token');
            
            if (!tokenCookie) {
                console.log('❌ No hay token en cookies, limpiando sesión');
                this.limpiarSesion();
                return;
            }
            
            console.log('✅ Token encontrado en cookies, verificando...');
            
            try {
                // Establecer el token en el store
                this.token = tokenCookie;
                
                // Verificar que el token sea válido
                const datosUsuario = await miUsuario();
                
                if (datosUsuario) {
                    this.establecerUsuario(datosUsuario);
                    console.log('✅ Sesión restaurada exitosamente:', {
                        usuario: datosUsuario?.email,
                        id: datosUsuario?.id
                    });
                } else {
                    console.log('❌ Token inválido, limpiando sesión');
                    this.cerrarSesion();
                }
            } catch (e) {
                console.error('❌ Error verificando sesión:', e);
                this.cerrarSesion();
            }
        },

        async verificarAutenticacion() {
            try {
                const usuario = await miUsuario();
                if (usuario) {
                    this.establecerUsuario(usuario);
                    return true;
                } else {
                    this.cerrarSesion();
                    return false;
                }
            } catch (e) {
                this.cerrarSesion();
                return false;
            }
        },

        async ingresarConGoogle(data, redirect = "/") {
            try {
                this.usuario = await loginGoogle(data);
                const authToken = `${this.usuario.id}:${this.usuario.token}`;
                const encodedToken = Buffer.from(authToken).toString("base64");
                this.token = encodedToken;
                this.isAuthenticated = true;
                this.usuario = await miUsuario();
                this.error = null;

                setCookie('orsai_token', this.token, 1, 'orsai.org');
                // Redireccionar a la página de inicio
                if (redirect) {
                    // Si tiene HTTP, es una URL externa
                    if (redirect.startsWith('http')) {
                        window.location.href = redirect;
                    } else if (redirect === 'no-redirect') {
                        this.loading = false;
                        return true;
                    } else {
                        router.push({ path: redirect });
                    }
                }
            } catch (e) {
                console.error(e);
                this.error = "No se pudo iniciar sesión con Google. Por favor, intentá nuevamente.";
            }
        },
        async cambiarContrasena(clave1, clave2, token) {
            try {
                this.loading = true;
                this.usuario = await nuevaClave({ clave1, clave2, token });
                // Loguearte con la nueva clave
                const authToken = `${this.usuario.id}:${this.usuario.token}`;
                const encodedToken = Buffer.from(authToken).toString("base64");
                this.token = encodedToken;
                this.isAuthenticated = true;

                this.usuario = await miUsuario();


                this.error = null;
                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.error = "No se pudo cambiar la contraseña. Por favor, intentá nuevamente.";
            }
        },
        async enviarLinkRecuperacion(correo) {
            try {
                this.loading = true;
                await recuperarClave(correo);
                this.error = null;
                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.error = "No se reconoce el correo ingresado. Por favor, intentá nuevamente.";
            }
        },
        async login(usuario, redirect = "/") {
            this.loading = true;
            this.error = null;

            console.log('Login', usuario);
            const response = await loginService(usuario);
            if (response.login) {
                this.usuario = response.usuario;
                this.isAuthenticated = true;

                // Calcular token
                let authToken = `${this.usuario.id}:${this.usuario.token}`;
                authToken = Buffer.from(authToken).toString('base64');
                this.token = authToken;

                setCookie('orsai_token', this.token, 1, 'orsai.org');
                // Redireccionar a la página de inicio
                console.log('Redirect', redirect);
                if(redirect) {
                    // Si es la misma ruta, forzar recarga
                    if (router.currentRoute.path === redirect) {
                        console.log('Forzando recarga');
                        router.go();
                    } else if (redirect.startsWith('http')) {
                        window.location.href = redirect;
                    }
                    else {
                        router.push({ path: redirect });
                    }
                } 
                this.loading = false;
                return true;
            } else {
                this.isAuthenticated = false;
                this.error = "Correo o contraseña incorrectos. Por favor, intentá nuevamente.";
                this.loading = false;
                return false;
            }
        },
        logout() {
            console.log('🚪 Haciendo logout...');
            
            // Limpiar estado del store
            this.limpiarSesion();
            
            // Limpiar cache
            resetPageLoadCache();
            
            // Limpiar cookies de manera exhaustiva
            console.log('🧹 Limpiando cookies...');
            
            // Método 1: Usando setCookie
            setCookie('orsai_token', '', -1, 'orsai.org');
            setCookie('orsai_token', '', -1, '');
            
            // Método 2: Usando document.cookie directamente
            document.cookie = 'orsai_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.orsai.org';
            document.cookie = 'orsai_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=orsai.org';
            document.cookie = 'orsai_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
            
            console.log('✅ Logout completado y cookies limpiadas');
            console.log('📄 Cookies después de limpiar:', document.cookie);

            router.push({ path: '/ingresar' });
        },
        async estaAutenticado() {
            // Verificar si el usuario está autenticado miUsuario
            try {
                const usuario = await miUsuario();
                if (usuario) {
                    this.usuario = usuario;
                    this.isAuthenticated = true;
                } else {
                    this.isAuthenticated = false;
                }
            } catch (e) {
                console.error("NO ESTÁ AUTENTICADO", e);
                this.isAuthenticated = false;
            }
            return this.isAuthenticated;
        },
        async cargarDatosAdicionales() {
            if (this.datosAdicionales) return; // Si ya tenemos los datos, no los volvemos a cargar
            
            try {
                const response = await strapiService.get(`/api/usuarios/${this.usuario.id}?populate=*`);
                this.datosAdicionales = {
                    fecha_nacimiento: response.data.fecha_nacimiento,
                    pais: response.data.pais,
                    // otros datos que necesites
                };
            } catch (error) {
                console.error('Error al cargar datos adicionales:', error);
            }
        },
        actualizarDatosAdicionales(datos) {
            this.datosAdicionales = { ...this.datosAdicionales, ...datos };
        },
        
        // Función de debug temporal
        debugCookies() {
            console.log('🔍 === DEBUG DE COOKIES ===');
            console.log('📄 Document.cookie completo:', document.cookie);
            console.log('🍪 orsai_token cookie:', getCookie('orsai_token'));
            console.log('🏪 Token en store:', this.token);
            console.log('👤 Usuario en store:', this.usuario);
            console.log('🔐 isAuthenticated:', this.isAuthenticated);
            console.log('📊 Estado completo del store:', {
                usuario: this.usuario,
                token: this.token,
                isAuthenticated: this.isAuthenticated,
                error: this.error
            });
            console.log('🔍 === FIN DEBUG ===');
        }
    },
    persist: {
        storage: localStorage,
        paths: ['usuario', 'isAuthenticated', 'datosAdicionales']
    }
});
